import React from 'react';
import { DonationConfiguration } from "./LineItemTypes"
import { GACntxt } from '../../../AllianceApp';

/**
 * Implementation for the donation line item type, which saves attribution data for a donation.
 */

/**
 * Props for the donationLineItem
 * lineItemName: Name of line item
 * itemConfiguration: Configuration of the donation line item
 * onDataChange: Callback that is called when the data provided in the form has changed.
 */
interface donationLineItemProps
{
    lineItemId: number
    lineItemName: string,
    itemConfiguration: DonationConfiguration,
    onDataChange : (lineItemId: number, newData: any, isValid: boolean, errorMsg?: string) => void,
    lineItemData: any,
    disableInputs ?: boolean
}

/**
 * The radio button options available for the line item.
 */
enum RadioButtonOptions {
    loggedInUser = 'loggedInUser',
    anonymous = 'anonymous',
    custom = 'custom'
}

//Since TS doesn't support reverse mapping, and radio button input converts to string...
//TODO: Optimize This so it doesn't have to be added to when new options are added.
const radioButtonOptionsReverseMap = (value: string) =>
{
    if(value == 'loggedInUser')
    {
        return RadioButtonOptions.loggedInUser;
    }
    if(value == 'anonymous')
    {
        return RadioButtonOptions.anonymous;
    }
    return RadioButtonOptions.custom;
}

interface donationData
{
    attributionType: RadioButtonOptions,
    customAttributionName: string
}


const DonationLineItem = (props: donationLineItemProps) => 
{
    const userContext = React.useContext(GACntxt);

    const dataProp = props.lineItemData?.attribution ? props.lineItemData.attribution as donationData : { 
        attributionType: (userContext.userTGAName) ? RadioButtonOptions.loggedInUser : RadioButtonOptions.anonymous,
        customAttributionName: ''
    };
    
    const [selectedOption, setSelectedOption] = React.useState<RadioButtonOptions>
        (dataProp.attributionType);
    const [ customInput, setCustomInput ] = React.useState<string>(dataProp.customAttributionName);
    const [ showCustomErrorMessage, setShowCustomErrorMessage ] = React.useState<boolean>(false);

    /**
     * Use Effect that changes the input on the loggedInUserName changing.
     * TODO: This is a green-wire fix for AllianceApp not waiting until it has identified the user logged in before rendering the page.
     * (Otherwise, the default option is selected before a username can be provided.)
     * Only change if there is no field in props.lineItemData.attribution
     */
    React.useEffect(() => 
    {
        if(props.lineItemData?.attribution === undefined)
        {
            setSelectedOption((userContext.userTGAName) ? RadioButtonOptions.loggedInUser : RadioButtonOptions.anonymous);
        }
    },[userContext.userTGAName])

    /**
     * Use effect that triggers when either the radio button or custom input field is changed.
     * Checks validity of data and invokes callback for parent to save.
     */
    React.useEffect(() => 
    {
        const isValid = selectedOption === RadioButtonOptions.custom ? (customInput !== ""): true;
        const data = {
            attribution: {
                attributionType: selectedOption,
                customAttributionName: customInput
            }
        }
        props.onDataChange(props.lineItemId, data, isValid, isValid ? undefined : `Waiting for ${props.lineItemName} attribution`);
    },[selectedOption, customInput]);

    /**
     * Handler for when the custom attribution input field is changed
     * @param event Event triggered by onChange
     */
    const handleCustomInput: React.ChangeEventHandler<HTMLInputElement> = (event) => 
    {
        const input = event.target.value;
        setCustomInput(input);
        setShowCustomErrorMessage(!(input !== ""));

    }

    /**
     * Handler for when the radio button options are changed
     * @param event Event triggered by onChange
     */
    const handleOptionChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        console.log(event.target.value);
        const selected = radioButtonOptionsReverseMap(event.target.value);
        setSelectedOption(selected);
        if (selected == RadioButtonOptions.custom)
        {
            setShowCustomErrorMessage(!(customInput !== ""));
        }
        else
        {
            setShowCustomErrorMessage(false);
        }

        
    }

    /**
     * Handler to change the validity of the data when custom input is focused,
     * so that form is not accidentally submitted while data is being input.
     */
    const clearCustomValidation = () => 
    {
        //Invalidate Custom Input for parent until data is finished being entered onBlur
        const isValid = selectedOption === RadioButtonOptions.custom ? false: true;
        const data = {
            attribution: {
                attributionType: selectedOption,
                customAttributionName: ''
            }
        }
        props.onDataChange(props.lineItemId, data, isValid, isValid ? undefined : `Waiting for ${props.lineItemName} attribution`);
    }

    return(
        <form className='checkoutPageDonationForm'>
            <p>Attribute to:</p>
            {userContext.userTGAName && <div>
                <div className='checkoutPageDonationRadioButtons'>
                    <input
                    type="radio"
                    id='1'
                    name={props.itemConfiguration.componentName.concat('radio')}
                    value={RadioButtonOptions.loggedInUser}
                    checked={selectedOption === RadioButtonOptions.loggedInUser}
                    disabled={props.disableInputs ?? false}
                    onChange={handleOptionChange}
                    />
                    <label htmlFor="1">
                        {userContext.userTGAName}
                    </label>
                </div>
            </div>}
            <div className='checkoutPageDonationRadioButtons'>
                <input
                type="radio"
                id='2'
                name={props.itemConfiguration.componentName.concat('radio')}
                value={RadioButtonOptions.anonymous}
                checked={selectedOption === RadioButtonOptions.anonymous}
                disabled={props.disableInputs ?? false}
                onChange={handleOptionChange}
                />
                <label htmlFor="2">
                    Anonymous
                </label>
            </div>
            <div className='checkoutPageDonationRadioButtons'>
                <input
                type="radio"
                id='3'
                name={props.itemConfiguration.componentName.concat('radio')}
                value={RadioButtonOptions.custom}
                checked={selectedOption === RadioButtonOptions.custom}
                disabled={props.disableInputs ?? false}
                onFocus={clearCustomValidation}
                onChange={handleOptionChange}
                />
                <label htmlFor="3">
                    <input
                        type='text'
                        name={props.itemConfiguration.componentName.concat('radio').concat('customInput')}
                        value={customInput}
                        disabled={props.disableInputs ?? false}
                        onChange={handleCustomInput}
                        placeholder='Custom Attribution'
                    />
                 </label>
                {showCustomErrorMessage && <p className='checkoutPageTextError'>Please provide an attribution name.</p>}
            </div>
        </form>
    )
}

export default DonationLineItem;